/* global chrome */
/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import PatientRowName from 'components/Misc/PatientRowName';
import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../api/GlobalContext';
import ReviewPrompt from './ReviewPrompt';
import { TemplateBuilderTutorial } from './tutorials/TemplateBuilderTutorial';
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  Col,
  Container,
  Row,
  Table
} from 'reactstrap';

import Header from 'components/Headers/Header.js';
import ConversationRowName from 'components/Misc/ConversationRowName';
import { GETTING_STARTED_URL_MAP } from '../constants/EmrNoteTypeMapping';

const MINIMAL_UI_FEATURE_FLAG = 'minimal_ui';
const Home = (props) => {
  const navigate = useNavigate();
  const [showTemplateBuilderTutorial, setShowTemplateBuilderTutorial] =
    useState(false);
  // Get our Globals
  const { apiClient } = useContext(GlobalContext);

  const minimalUIFeatureFlag =
    apiClient?.features?.[MINIMAL_UI_FEATURE_FLAG] || false;

  const [userRole, setUserRole] = useState(null);
  useEffect(() => {
    apiClient
      .getStats({})
      .then((data) => {
        if (data?.role) setUserRole(data.role);
      })
      .catch(() => {});
  }, [apiClient]);

  // Show Minimal UI handler
  const handleShowMinimalUI = () => {
    console.log('Show Minimal UI button clicked');
    const channelName = 'NOTIFICATION_MINIMAL_UI_CHANNEL';
    const userInfo = {
      name: apiClient.name
    };
    apiClient.sendSlackMessage('Minimal UI enabled', channelName, userInfo);
    // Get the current active tab and enable minimal UI
    chrome.tabs.query({ active: true, currentWindow: true }, (tabs) => {
      console.log('Found tabs:', tabs);
      if (tabs[0]?.id) {
        console.log('Sending TOGGLE_MINIMAL_UI to tab:', tabs[0].id);
        // Enable minimal UI on the current tab - it will handle closing the side panel
        chrome.tabs.sendMessage(tabs[0].id, {
          type: 'TOGGLE_MINIMAL_UI',
          enabled: true
        });
      }
    });
  };

  // If our session key is null we should redirect to login
  // Otherwise load the homepage
  useEffect(() => {
    if (!apiClient.haveSessionKey()) {
      console.error('Went home and got null session key. Forwarding to Login');
      navigate('/auth/login');
    }
  }, [apiClient, navigate]);

  // Update the user patients and conversations the first time this component loads
  const [patients, setPatients] = useState([]);
  const [conversations, setConversations] = useState([]);

  useEffect(() => {
    // Go get the conversations for this user
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    apiClient
      .updateConversations(
        null,
        sevenDaysAgo.toISOString(),
        today.toISOString()
      )
      .then((data) => {
        // only post the most recent 5 conversations
        setConversations(apiClient.conversations.slice(0, 5));
      })
      .catch((error) => {
        // probably no session key
        // console.log(error);
        console.error('Did not get visits');
      });
    apiClient
      .updatePatients()
      .then((data) => {
        // only post the most recent 5 patients
        var localPatients = apiClient.patients;
        localPatients.sort((a, b) => {
          if (a.lastUsed < b.lastUsed) {
            return 1;
          } else if (a.lastUsed > b.lastUsed) {
            return -1;
          } else {
            return 0;
          }
        });
        setPatients(localPatients.slice(0, 5));
      })
      .catch((error) => {
        // probably no session key
        // console.log(error);
        console.error('Did not get patients');
      });
  }, [apiClient]);

  // The first-run slide-deck tutorial is gone — the onboarding checklist is
  // the guide now.
  useEffect(() => {
    // Show new feature alert only to existing users
    if (
      localStorage.getItem('home-tutorial') &&
      localStorage.getItem('template-builder-tutorial') === null
    ) {
      setShowTemplateBuilderTutorial(false); //not need to show this tutorial anymore
    }
  }, []);

  return (
    <>
      <Header />
      {/* Page content */}
      {showTemplateBuilderTutorial && <TemplateBuilderTutorial />}
      {/* Review Prompt */}
      <ReviewPrompt shouldCheckOnMount={true} />
      <Container className="mt--3" fluid>
        {/* Getting Started Card */}
        <Row className="mb-2">
          <Col>
            <Card
              className="shadow-sm"
              style={{ border: '1px solid #e9ecef' }}
            >
              <div
                style={{
                  padding: '12px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <span style={{ fontWeight: '500', fontSize: '14px', color: '#6c757d' }}>
                    Getting Started
                  </span>
                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                    New to Comprehend? Learn how it works here!
                  </div>
                </div>
                <a
                  href={GETTING_STARTED_URL_MAP[apiClient.EMR] || GETTING_STARTED_URL_MAP.default}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    color="primary"
                    size="sm"
                    className="get-started text-secondary"
                    style={{ fontSize: '12px', width: '90px', borderWidth: '2px', fontWeight: '600', marginLeft: '0.5em' }}
                  >
                    Get Started
                  </Button>
                </a>
              </div>
            </Card>
          </Col>
        </Row>
        {/* Invite Team Card */}
        {apiClient.active && (userRole === 'Admin' || userRole === 'Coordinator') && (
          <Row className="mb-2">
            <Col>
              <Card
                className="shadow-sm"
                style={{ border: '1px solid #e9ecef' }}
              >
                <div
                  style={{
                    padding: '12px 20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>
                      Invite Your Team
                    </span>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        marginTop: '2px'
                      }}
                    >
                      Looks like you're getting the hang of it! Add your team
                      risk-free — 30-day money-back guarantee.
                    </div>
                  </div>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => navigate('/User/admin/team')}
                    style={{
                      fontSize: '12px',
                      minWidth: '90px',
                      borderWidth: '2px',
                      fontWeight: '600',
                      marginLeft: '0.5em'
                    }}
                  >
                    Invite
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        )}
        {/* Beta Minimal UI Card */}
        {minimalUIFeatureFlag && (
          <Row className="mt-4 mb-2">
            <Col>
              <Card
                className="shadow-sm"
                style={{ border: '1px solid #e9ecef' }}
              >
                <div
                  style={{
                    padding: '12px 20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <span
                      style={{
                        color: '#dc3545',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}
                    >
                      Beta:
                    </span>
                    <span
                      style={{
                        fontWeight: '500',
                        fontSize: '14px',
                        marginLeft: '6px'
                      }}
                    >
                      Minimal UI
                    </span>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        marginTop: '2px'
                      }}
                    >
                      Start visits, record and Comprehend directly from your EMR
                      without opening the extension
                    </div>
                  </div>
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={handleShowMinimalUI}
                    style={{
                      fontSize: '12px',
                      minWidth: '70px',
                      borderWidth: '2px',
                      fontWeight: '600'
                    }}
                  >
                    Try it
                  </Button>
                </div>
              </Card>
            </Col>
          </Row>
        )}

        <Row className="mt-3">
          <Col xl="6">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <div className="col">
                    <h3 className="mb-0">Visits</h3>
                  </div>
                  <div className="col text-right">
                    <Link to="/User/conversations">
                      <Button color="primary" size="sm">
                        See all
                      </Button>
                    </Link>
                  </div>
                </Row>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">Last Used</th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.map((conversation) => (
                    <tr
                      key={conversation.conversationId}
                      onClick={() =>
                        navigate(
                          `/User/conversations/details#${conversation.conversationId}`
                        )
                      }
                      className="clickable-row"
                    >
                      <ConversationRowName conversation={conversation} />
                      <td>{conversation.lastUsed}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </Col>
          <Col xl="6">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <div className="col">
                    <h3 className="mb-0">Patients</h3>
                  </div>
                  <div className="col text-right">
                    <Link to="/User/patients">
                      <Button color="primary" size="sm">
                        See all
                      </Button>
                    </Link>
                  </div>
                </Row>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Patient name</th>
                    <th scope="col">Visits</th>
                    <th scope="col">Last Used</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((patient) => (
                    <tr
                      key={patient.uid}
                      onClick={() =>
                        navigate(`/User/conversations#${patient.uid}`)
                      }
                      className="clickable-row"
                    >
                      <PatientRowName
                        fName={patient.firstName}
                        lName={patient.lastName}
                        dob={patient.dob}
                      />
                      <td>
                        {patient.conversationIds
                          ? patient.conversationIds.length
                          : null}
                      </td>
                      <td>{patient.lastUsed}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Home;
